import { useLoading } from "../context/LoadingContext";

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../css/RelatorioProducao.css";
import {
  FaArrowLeft,
  FaSearch,
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import SelectBoxResponsavelPesquisa from "./SelectBoxResponsavelPesquisa";
import { useUser } from "../context/UserContext";
import BackButton from "../components/BackButton";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

registerLocale("pt-BR", ptBR);

const RelatorioProducao = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useUser();
  const { setIsLoading } = useLoading();

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [dataInicio, setDataInicio] = useState(firstDayOfMonth);
  const [dataFim, setDataFim] = useState(lastDayOfMonth);
  const [responsavelSelecionado, setResponsavelSelecionado] = useState("");
  const [nomeItem, setNomeItem] = useState("");
  const [chartData, setChartData] = useState([]);
  const [itemNames, setItemNames] = useState([]);
  const [biAnalysis, setBiAnalysis] = useState(null);
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);

  useEffect(() => {
    handleSearch();
  }, []);

  const handleSearch = async () => {
    const toastId = toast.loading("Buscando relatório...");
    setIsLoading(true);

    const inicio = dataInicio || firstDayOfMonth;
    const fim = dataFim || lastDayOfMonth;

    const formattedDataInicio = inicio.toISOString().split("T")[0];
    const formattedDataFim = fim.toISOString().split("T")[0];

    let apiUrl = `${process.env.REACT_APP_BASE_URL}/api/v1/relatorios/producao?idRestaurante=${id}`;
    apiUrl += `&dataInicio=${formattedDataInicio}`;
    apiUrl += `&dataFim=${formattedDataFim}`;

    if (nomeItem) {
      apiUrl += `&nomeItem=${nomeItem}`;
    }
    if (responsavelSelecionado) {
      apiUrl += `&idResponsavel=${responsavelSelecionado}`;
    }

    try {
      const token = user?.access_token;
      if (!token) {
        console.error("Token de autenticação não encontrado.");
        toast.update(toastId, {
          render: "Token de autenticação não encontrado.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        setIsLoading(false);
        return;
      }

      const response = await fetch(apiUrl, {
        headers: {
          Authentication: token,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const rawData = await response.json();

      // Step 1: Get all unique item names from the entire dataset
      const allItemNames = new Set();
      if (rawData && rawData.detalhes) {
        rawData.detalhes.forEach((detalhe) => {
          detalhe.itens.forEach((item) => {
            allItemNames.add(item.itemNome);
          });
        });
      }
      const itemNamesArray = Array.from(allItemNames);
      setItemNames(itemNamesArray);

      // Step 2: Create a lookup map for existing data
      const dataMap = new Map();
      if (rawData && rawData.detalhes) {
        rawData.detalhes.forEach((detalhe) => {
          const entry = {
            data: new Date(detalhe.data).toLocaleDateString("pt-BR"),
          };
          detalhe.itens.forEach((item) => {
            entry[item.itemNome] = item.quantidade;
          });
          dataMap.set(detalhe.data, entry);
        });
      }

      // Step 3: Generate a complete data series for the date range
      const fullDateRangeData = [];
      let currentDate = new Date(inicio);
      const endDate = new Date(fim);

      while (currentDate <= endDate) {
        const dateString = currentDate.toISOString().split("T")[0];
        const dayDataFromApi = dataMap.get(dateString);

        const completeDayData = { data: dateString };
        itemNamesArray.forEach((name) => {
          completeDayData[name] = (dayDataFromApi && dayDataFromApi[name]) || 0;
        });

        fullDateRangeData.push(completeDayData);
        currentDate.setDate(currentDate.getDate() + 1);
      }

      setChartData(fullDateRangeData);

      if (rawData && rawData.resumo) {
        setBiAnalysis({
          totalProduzido: rawData.resumo.totalItensProduzidos || 0,
        });
      }
      toast.update(toastId, {
        render: "Relatório carregado com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Erro ao buscar relatório:", error);
      setChartData([]); // Clear data on error
      toast.update(toastId, {
        render: "Erro ao buscar relatório.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const lineColors = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff8042",
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
  ];

  return (
    <div className="relatorio-producao-container">
      <ToastContainer />
      <BackButton inline />
      <h2>Relatório de Produção</h2>
      <div className="filtro-container-producao">
        <div className="filtro-group">
          <label>Data Início:</label>
          <DatePicker
            selected={dataInicio}
            onChange={(date) => setDataInicio(date)}
            dateFormat="dd/MM/yyyy"
            locale="pt-BR"
            placeholderText="Selecione a data de início"
            className="date-picker"
          />
        </div>
        <div className="filtro-group">
          <label>Data Fim:</label>
          <DatePicker
            selected={dataFim}
            onChange={(date) => setDataFim(date)}
            dateFormat="dd/MM/yyyy"
            locale="pt-BR"
            placeholderText="Selecione a data de fim"
            className="date-picker"
          />
        </div>
        <div className="filtro-group">
          <label>Nome do Item:</label>
          <input
            type="text"
            value={nomeItem}
            onChange={(e) => setNomeItem(e.target.value)}
            placeholder="Digite o nome do item"
            className="text-input"
          />
        </div>
        <div className="filtro-group">
          <label>Responsável:</label>
          <SelectBoxResponsavelPesquisa
            value={responsavelSelecionado}
            onChange={setResponsavelSelecionado}
          />
        </div>
        <button
          onClick={handleSearch}
          className={isLoadingLocal ? "search-button-loading" : "search-button"}
          disabled={isLoadingLocal}
        >
          <FaSearch /> {isLoadingLocal ? "Buscando..." : "Buscar"}
        </button>
      </div>

      <div className="relatorio-grafico-container">
        <h3>Análise de Produção</h3>
        {isLoadingLocal ? (
          <div className="loading-message">
            Carregando dados do relatório...
          </div>
        ) : chartData.length > 0 ? (
          <>
            <div className="bi-metrics">
              <p>
                <strong>Total Produzido:</strong>{" "}
                {biAnalysis?.totalProduzido.toFixed(2)}
              </p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={chartData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="data" />
                <YAxis />
                <Tooltip />
                <Legend />
                {itemNames.map((name, index) => (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={lineColors[index % lineColors.length]}
                    name={name}
                    strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </>
        ) : (
          <p className="no-data-message">
            Nenhum dado de produção encontrado para os filtros selecionados.
          </p>
        )}
      </div>
    </div>
  );
};

export default RelatorioProducao;
